<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ChatMessage, ContactRef } from '../../api/types'
import { clockTime, UNSENT_LABEL } from '../../lib/inbox'
import { isLocalOnly, sendErrorOf, sendStatusOf, type UiSendFlags } from '../../lib/send'
import type { StaffInfo } from '../../stores/inbox'
import Avatar from '../ui/Avatar.vue'
import Icon from '../ui/Icon.vue'
import MediaViewer from '../ui/MediaViewer.vue'
import PhoneLink from './PhoneLink.vue'
import SlipCheck from './SlipCheck.vue'
import LocationCard from './LocationCard.vue'
import FbLazyMedia from './FbLazyMedia.vue'
import MenuReadAction from './MenuReadAction.vue'

const props = defineProps<{
  item: ChatMessage & UiSendFlags
  contactInitial: string
  /** The chat's customer (picture); null in LINE groups, where each message carries its member. */
  contact?: ContactRef | null
  /** Avatar info for a staff author. */
  staff: StaffInfo | null
  showAvatar: boolean
  showMeta: boolean
  tight: boolean
}>()

defineEmits<{ dismiss: [id: string]; retry: [id: string] }>()

/** pending = กำลังส่ง · sent = ส่งแล้ว · failed = ส่งไม่สำเร็จ (+ reason, ลองใหม่) · null for inbound */
const status = computed(() => sendStatusOf(props.item))
const sendError = computed(() => (status.value === 'failed' ? sendErrorOf(props.item) : null))
const localOnly = computed(() => isLocalOnly(props.item))
const side = computed(() => {
  const a = props.item.author
  if (props.item.direction === 'in') return 'in'
  return a.kind === 'ai' || a.kind === 'auto' ? 'ai' : 'me'
})
/** The sender unsent it (api/app/unsend.py): only a muted placeholder — no content, no actions on it. */
const unsent = computed(() => props.item.type === 'unsent')
const unsentLabel = computed(() => (props.item.direction === 'out' ? UNSENT_LABEL.out : UNSENT_LABEL.in))
/** An automatic message written by code (question-button answer): its own label instead of "AI ตอบแทน". */
const autoLabel = computed(() => (props.item.author.kind === 'auto' ? props.item.author.label : null))
/** "ตอบอัตโนมัตินอกเวลาทำการ" (api/app/out_of_hours.py): not a reply — the chat still waits for a person, never re-sent. */
const notice = computed(() => props.item.author.kind === 'auto' && props.item.author.reply === false)
// every message shows in full, however long — nothing is folded (owner 18 ต.ค. 69 "ไม่ต้องย่อข้อความใดๆ")
const authorName = computed(() => {
  const a = props.item.author
  if (a.kind === 'staff') return a.staff.name
  if (a.kind === 'page') return 'ตอบจากแอปอื่น'
  return ''
})
/** Who wrote an inbound message: the group member (LINE group/room) or the chat's customer. */
const sender = computed<ContactRef | null>(() => {
  const a = props.item.author
  return (a.kind === 'contact' ? a.contact : null) ?? props.contact ?? null
})
const memberName = computed(() => {
  const a = props.item.author
  return a.kind === 'contact' && a.contact ? a.contact.name : ''
})
const aiTopic = computed(() => (props.item.author.kind === 'ai' ? props.item.author.topic : null))
const time = computed(() => clockTime(props.item.at))

/** Plain text → text/link parts. Links open in a new tab; nothing is rendered as HTML. */
const LINK = /((?:https?:\/\/)?(?:[a-z0-9-]+\.)+(?:com|cloud|co|th|net|org|me|app|io)(?:\/[^\s]*)?)/gi
const parts = computed(() => {
  const text = props.item.text ?? ''
  const out: { t: 'text' | 'link'; v: string; href?: string }[] = []
  let last = 0
  for (const m of text.matchAll(LINK)) {
    const i = m.index ?? 0
    if (i > last) out.push({ t: 'text', v: text.slice(last, i) })
    const v = m[0]
    out.push({ t: 'link', v, href: /^https?:\/\//i.test(v) ? v : `https://${v}` })
    last = i + v.length
  }
  if (last < text.length) out.push({ t: 'text', v: text.slice(last) })
  return out
})
/** A sticker is shown as a picture without a bubble (LINE: API builds the CDN link · Messenger: its own image). */
const sticker = computed(() => (props.item.type === 'sticker' ? (props.item.attachments.find((a) => a.url) ?? null) : null))
const stickerBroken = ref(false)
/** A shared location with coordinates → map card (owner 27 ก.ย. 69: "line ปกติส่ง location ได้"). */
const place = computed(() =>
  props.item.type === 'location' ? (props.item.attachments.find((a) => a.type === 'location' && a.lat != null && a.lng != null) ?? null) : null,
)
const showSticker = computed(() => !!sticker.value && !stickerBroken.value)
const media = computed(() =>
  props.item.type === 'sticker' ? [] : props.item.attachments.filter((a) => a.type === 'image' || a.type === 'file' || a.type === 'video'),
)
const mediaLabel: Record<string, string> = { image: 'รูปภาพ', video: 'วิดีโอ', file: 'ไฟล์' }
/** Images/videos with a URL open in the full-screen viewer; the bubble frame is dropped when there is no text. */
const viewable = computed(() =>
  media.value.filter((a): a is typeof a & { url: string } => !!a.url && (a.type === 'image' || a.type === 'video')),
)
const mediaOnly = computed(
  () => showSticker.value || (!props.item.text && media.value.length > 0 && media.value.every((a) => a.url && a.type !== 'file')),
)
const broken = ref<Set<number>>(new Set())
const viewerAt = ref<number | null>(null)
function openViewer(a: { url?: string | null }): void {
  const i = viewable.value.findIndex((v) => v.url === a.url)
  if (i >= 0) viewerAt.value = i
}
function markBroken(i: number): void {
  broken.value = new Set(broken.value).add(i)
}
/** Imported Facebook history (api/app/fb_history.py): older files load on click, no slip check on old pictures. */
const fromFbHistory = computed(() => props.item.attachments.some((a) => a.fetch === 'meta'))
const typeLabel: Partial<Record<ChatMessage['type'], string>> = {
  sticker: '[สติกเกอร์]',
  audio: '[ข้อความเสียง]',
  location: '[ตำแหน่งที่ตั้ง]',
  unknown: '[ข้อความที่ระบบยังไม่รองรับ]',
}
</script>

<template>
  <div class="msg" :class="[side === 'in' ? 'in' : 'out', { tight }]">
    <Avatar
      v-if="side === 'in'"
      :class="{ 'is-hidden': !showAvatar }"
      :initial="sender?.initial ?? contactInitial"
      :url="sender?.avatar_url ?? null"
      :contact-id="sender?.id ?? null"
      :size="28"
    />

    <div class="bw">
      <div v-if="side === 'ai' && !tight && autoLabel" class="ai-tag auto-tag"><Icon :name="notice ? 'clock' : 'zap'" /><b>{{ autoLabel }}</b></div>
      <div v-else-if="side === 'ai' && !tight" class="ai-tag">
        <Icon name="sparkles" /><b>AI ตอบแทน</b><template v-if="aiTopic"><span class="ai-dot">·</span><span>{{ aiTopic }}</span></template>
      </div>

      <div v-if="unsent" class="bub unsent" role="note"><span class="unsent-t"><Icon name="undo" />{{ unsentLabel }}</span></div>
      <div
        v-else
        class="bub"
        :class="{ ai: side === 'ai', me: side === 'me', pending: status === 'pending', failed: status === 'failed', 'media-only': mediaOnly }"
      >
        <template v-for="(a, i) in media" :key="i">
          <button
            v-if="a.url && a.type === 'image' && !broken.has(i)"
            type="button"
            class="ph"
            :title="'กดเพื่อดูรูปขนาดใหญ่'"
            :aria-label="`ดู${a.name ? ' ' + a.name : 'รูปภาพ'}ขนาดใหญ่`"
            @click="openViewer(a)"
          >
            <img :src="a.url" :alt="a.name ?? 'รูปภาพ'" loading="lazy" @error="markBroken(i)" />
          </button>
          <button
            v-else-if="a.url && a.type === 'video' && !broken.has(i)"
            type="button"
            class="ph vid"
            aria-label="เปิดวิดีโอ"
            @click="openViewer(a)"
          >
            <video :src="a.url" :poster="a.preview_url ?? undefined" preload="metadata" muted playsinline @error="markBroken(i)"></video>
            <span class="play" aria-hidden="true"><Icon name="play" /></span>
          </button>
          <FbLazyMedia
            v-else-if="a.fetch === 'meta'"
            :message-id="item.id"
            :idx="item.attachments.indexOf(a)"
            :att="a"
            :mine="side === 'me'"
          />
          <div v-else class="att">
            <Icon name="image" /><span
              >{{ mediaLabel[a.type] ?? 'ไฟล์' }}<template v-if="a.name"> · {{ a.name }}</template
              ><template v-if="broken.has(i)"> · เปิดไม่ได้ (ลิงก์หมดอายุ)</template></span
            >
          </div>
        </template>
        <span v-if="item.text" class="txt"
          ><template v-for="(p, i) in parts" :key="i"
            ><a v-if="p.t === 'link'" class="lnk" :href="p.href" target="_blank" rel="noopener noreferrer">{{ p.v }}</a
            ><template v-else>{{ p.v }}</template></template
          ></span
        >
        <img
          v-else-if="showSticker && sticker"
          class="stk"
          :src="sticker.url!"
          alt="สติกเกอร์"
          title="สติกเกอร์"
          loading="lazy"
          @error="stickerBroken = true"
        />
        <LocationCard v-else-if="place" :place="place" />
        <span v-else-if="typeLabel[item.type]" class="muted">{{ typeLabel[item.type] }}</span>
      </div>
      <span v-if="item.tag && !unsent" class="mtag" :class="item.tag.tone"><Icon name="tag" />{{ item.tag.text }}</span>

      <SlipCheck v-if="item.direction === 'in' && item.type === 'image' && !localOnly && !fromFbHistory" :item="item" />
      <!-- a phone number the customer typed → "ผูกเบอร์ … กับลูกค้า POS" (lib/phone.ts, stores/phonelink.ts) -->
      <PhoneLink v-if="item.direction === 'in' && item.type === 'text' && !localOnly" :conv-id="item.conversation_id" :text="item.text" />
      <!-- อ่านเมนูจากแชท: a customer text run that names menus / every customer picture → recheck modal → ลงออเดอร์ -->
      <MenuReadAction v-if="item.direction === 'in' && (item.type === 'text' || item.type === 'image') && !localOnly && !fromFbHistory" :item="item" />

      <div v-if="showMeta || status === 'pending' || status === 'failed'" class="meta">
        <template v-if="side === 'ai'">
          <span>ป้ายนี้เห็นเฉพาะพนักงาน</span><span class="dotsep"></span>
        </template>
        <template v-else-if="side === 'me' && authorName">
          <span>{{ authorName }}</span><span class="dotsep"></span>
        </template>
        <template v-else-if="side === 'in' && memberName">
          <span class="who-in">{{ memberName }}</span><span class="dotsep"></span>
        </template>
        <span class="mono">{{ time }}</span>
        <template v-if="side !== 'in'">
          <span v-if="status === 'pending'" class="sst">กำลังส่ง…</span>
          <span v-else-if="status === 'failed'" class="sst bad"
            ><Icon name="alertCircle" />ส่งไม่สำเร็จ
            <button v-if="!notice" class="act" @click="$emit('retry', item.id)">ลองใหม่</button>
            <button v-if="localOnly && item.refused" class="dismiss" @click="$emit('dismiss', item.id)">ซ่อน</button></span
          >
          <span
            v-else
            :title="item.read ? 'ลูกค้าอ่านแล้ว' : 'ส่งแล้ว'"
            :aria-label="item.read ? 'ลูกค้าอ่านแล้ว' : 'ส่งแล้ว'"
            class="rd"
            :class="{ on: item.read }"
            ><Icon :name="item.read ? 'checks' : 'check'"
          /></span>
        </template>
      </div>
      <p v-if="sendError" class="send-err" role="status">{{ sendError }}</p>
    </div>

    <span v-if="side === 'ai'" class="av av-sm av-ai" :class="{ 'is-hidden': !showAvatar }" aria-hidden="true"
      ><Icon :name="notice ? 'clock' : autoLabel ? 'zap' : 'sparkle'"
    /></span>
    <span
      v-else-if="side === 'me'"
      class="av av-sm"
      :class="[staff?.tone ?? 's-w', { 'is-hidden': !showAvatar }]"
      aria-hidden="true"
      >{{ staff?.initial ?? '·' }}</span
    >
    <MediaViewer v-if="viewerAt !== null" :items="viewable" :start="viewerAt" @close="viewerAt = null" />
  </div>
</template>

<style scoped>
.msg {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.msg.out {
  justify-content: flex-end;
}
.msg.tight {
  margin-top: -8px;
}
.av-sm {
  width: 28px;
  height: 28px;
  font-size: 12px;
}
.av-sm :deep(.ic) {
  width: 15px;
  height: 15px;
}
.is-hidden {
  visibility: hidden;
}
.bw {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: min(448px, 78%);
  min-width: 0;
}
.msg.out .bw {
  align-items: flex-end;
}
.bub {
  padding: 9px 13px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 22px;
  background: #fff;
  border: 1px solid #e2e9e5;
  color: var(--ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.msg.in .bub {
  border-bottom-left-radius: 5px;
}
.bub.ai {
  background: #e3f1ea;
  border: 1px dashed #7fbfa2;
  border-bottom-right-radius: 5px;
}
.bub.me {
  background: var(--ac);
  border-color: var(--ac);
  color: #fff;
  border-bottom-right-radius: 5px;
}
.bub.pending {
  opacity: 0.7;
}
.bub.failed {
  background: #fff;
  border-color: #f2c7c2;
  color: var(--ink);
}
.lnk {
  color: var(--ac2);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.bub.me .lnk {
  color: #fff;
}
.att {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  height: 64px;
  padding: 0 12px;
  border-radius: 8px;
  background: #f4f6f5;
  color: var(--mut);
  font-size: 13px;
  line-height: 20px;
}
/* LINE/Messenger sticker: transparent picture, no bubble, like in the apps */
.stk {
  display: block;
  width: 120px;
  height: 120px;
  object-fit: contain;
}
/* A photo keeps its own proportions; the button hugs it so no frame shows around it. */
.ph {
  position: relative;
  display: block;
  padding: 0;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
  background: #eef2f0;
  cursor: zoom-in;
  line-height: 0;
  max-width: 100%;
}
.ph img,
.ph video {
  display: block;
  width: auto;
  height: auto;
  max-width: min(280px, 100%);
  max-height: 320px;
  min-width: 80px;
  min-height: 60px;
  object-fit: cover;
}
.ph:focus-visible {
  outline: 2px solid var(--ac);
  outline-offset: 2px;
}
.ph.vid {
  cursor: pointer;
}
.play {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: rgba(12, 18, 15, 0.6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.play :deep(.ic) {
  width: 18px;
  height: 18px;
}
/* unsent by the sender: a quiet placeholder on either side, never the green "sent" bubble */
.bub.unsent {
  background: #f4f6f5;
  border: 1px dashed #cdd8d2;
  color: var(--mut);
  font-size: 13px;
  line-height: 20px;
}
.unsent-t {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.unsent-t :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
}
.bub.media-only {
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 12px;
}
.bub.me .att {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}
/* a failed bubble turns white: its link and file box must not stay white-on-white */
.bub.me.failed .lnk {
  color: var(--ac2);
}
.bub.me.failed .att {
  background: #f4f6f5;
  color: var(--mut);
}
.ai-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px 0 8px;
  border-radius: 999px;
  background: #17211c;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}
.ai-tag :deep(.ic) {
  width: 14px;
  height: 14px;
  color: #86e3bc;
}
.ai-tag b {
  font-weight: 600;
}
.ai-dot {
  color: #9fb3a9;
}
.auto-tag {
  background: #eef3f0;
  color: var(--ink);
  border: 1px solid #d5e2db;
}
.auto-tag :deep(.ic) {
  color: var(--ac2);
}
/* a question-button tap of a web visitor (staff only) */
.mtag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 1px 8px 1px 6px;
  border-radius: 999px;
  background: #eef2f0;
  color: var(--mut);
  font-size: 12px;
  line-height: 20px;
  white-space: normal;
}
.mtag.warn {
  background: #fdf1e3;
  color: #8a4b00;
  font-weight: 600;
}
.mtag :deep(.ic) {
  width: 13px;
  height: 13px;
  flex: none;
}
.meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--mut);
  line-height: 18px;
  padding: 0 4px;
  white-space: nowrap;
}
.meta .mono {
  font-size: 12px;
}
.rd {
  display: inline-flex;
  color: #8a9a92;
}
.rd.on {
  color: var(--ac);
}
.rd :deep(.ic),
.sst :deep(.ic) {
  width: 14px;
  height: 14px;
}
.sst {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.sst.bad {
  color: var(--crit);
  font-weight: 600;
}
.dismiss {
  margin-left: 4px;
  color: var(--mut);
  font-weight: 500;
  text-decoration: underline;
}
.act {
  margin-left: 6px;
  padding: 0 8px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #f2c7c2;
  background: #fff;
  color: var(--crit);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}
.act:hover {
  background: #fdf1ef;
}
.send-err {
  margin: 0;
  max-width: 100%;
  padding: 0 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--crit);
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: right;
}
</style>
